import React, { useContext, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import _ from "lodash";

import { RouteComponentProps } from "react-router";
import { connect } from "react-redux";
import { Card, Typography } from "@material-ui/core";

import { UIMessageContext } from "../../components/UIMessage";
import { PageHeader, PageRoot, Button } from "../../components";
import routes from "../../constants/routes";
import { RegulatoryInstrument } from "../../constants/interfaces";
import { regulatoryInstrumentActions } from "../../reducers/regulatoryInstrument";
import FormNumberField from "../../components/Fields/FormNumberField";
import { RxForm } from "../../components/Form";
import { SubmitButton } from "../../components/Form/SubmitButton";
import { ResetButton } from "../../components/Form/ResetButton";
import { INFORMATION_ACCURACY_CONFIRMATION } from "../../constants/messages";
import DatePickerField from "../../components/Fields/DatePickerField";
import api from "../../utils/api";

const Row: React.FC<{
  name: string;
  productName: string;
  quantityLicenced: string;
}> = ({ name, productName, quantityLicenced }) => {
  if (!productName) return null;

  return (
    <div>
      <div
        style={{ padding: "1em 1.5em", wordBreak: "break-all", marginTop: 12 }}
      >
        {productName}
      </div>
      <div
        style={{ padding: "1em 1.5em", wordBreak: "break-all", marginTop: 12 }}
      >
        {quantityLicenced}
      </div>
      <div
        style={{
          padding: "1em 1.5em",
          wordBreak: "break-all"
        }}
      >
        <FormNumberField
          label=""
          id={name}
          name={name}
          required
          style={{ width: "100%" }}
          inputRegxString='^(\d){0,10}$'
          validate={value => {
            const num = Number(value);
            if (Number.isNaN(num)) return "Invalid input";
            if (num < 0) return "Quantity entered must be greater than 0";
            return "";
          }}
          checkWarning={value => {
            const quantity = Number(quantityLicenced);
            if (Number.isNaN(quantity)) return "Invalid quantity";

            if (quantity < Number(value))
              return "Quantity entered greater than Licensed Quantity";
            return "";
          }}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  buttonWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
    width: "100%"
  },
  content: {
    display: "flex",
    flexDirection: "column",
    maxWidth: 900,
    margin: "auto"
  },
  formHeader: {
    display: "flex",
    flexWrap: "wrap"
  },
  formHeaderItem: {
    padding: theme.spacing(2),
    maxWidth: 300,
    wordBreak: "break-word"
  },
  formWrapper: {
    padding: theme.spacing(4)
  },
  select: {
    minWidth: "240px",
    margin: "1.5em"
  },
  title: {
    marginBottom: theme.spacing(4)
  },
  gridWrapper: {
    alignSelf: "stretch",
    overflowX: "auto"
  },
  grid: {
    display: "flex",
    minWidth: 600,
    flexDirection: "column",
    "&>div": {
      display: "flex",
      "&>div:nth-of-type(1)": {
        flex: 1
      },
      "&>div:nth-of-type(2)": {
        width: 180
      },
      "&>div:nth-of-type(3)": {
        width: 200
      }
    }
  },
  gridItemText: {
    padding: "1em 1.5em",
    wordBreak: "break-all",
    marginTop: 12
  },
  gridItem: {
    padding: "1em 1.5em",
    wordBreak: "break-all"
  }
}));

type Props = RouteComponentProps & {
  regulatoryInstrument: RegulatoryInstrument;
  getInstrument: (id: string) => Promise<void>;
  instrumentTypes: any;
};

const ActiveRegulatoryInstrumentList: React.FC<Props> = ({
  match,
  regulatoryInstrument,
  instrumentTypes,
  getInstrument,
  history
}) => {
  const classes = useStyles();

  const { showMessage } = useContext(UIMessageContext);
  useEffect(() => {
    const {
      params: { id }
    }: any = match;
    if (id) {
      getInstrument(id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!regulatoryInstrument) return null;

  return (
    <PageRoot>
      <PageHeader
        label={routes.IMPORT_EXPORT_LIST.label}
        breadcrumbLinks={[
          {
            label: routes.HOME.label,
            route: routes.HOME.url
          }
        ]}
      />
      <div className={classes.content}>
        <Typography
          className={classes.title}
          color="textSecondary"
          variant="h6"
        >
          Notify Controlled Drugs Imported or Exported
        </Typography>
        <Card className={classes.formWrapper}>
          {regulatoryInstrument ? (
            <RxForm
              confirmMessage={INFORMATION_ACCURACY_CONFIRMATION}
              onSubmit={async (value, meta, { resetForm }) => {
                const riDetail = {
                  ...regulatoryInstrument,
                  cdImportExport: {
                    ...value
                  }
                };
                try {
                  await api.put(`/ri/updateCDImportExport`, riDetail);

                  history.push(`${routes.IMPORT_EXPORT_LIST.url}`);
                  showMessage("Submitted successfully", "success");
                } catch (error) {
                  showMessage(
                    _.get(error, "response.data.message") || error.message,
                    "error"
                  );
                }
              }}
            >
              <div className={classes.formHeader}>
                <div className={classes.formHeaderItem}>
                  <Typography color="textSecondary" variant="caption">
                    Legal Entity
                  </Typography>
                  <Typography>{regulatoryInstrument.legalEntity}</Typography>
                </div>
                <div className={classes.formHeaderItem}>
                  <Typography color="textSecondary" variant="caption">
                    Premises
                  </Typography>
                  <Typography>{regulatoryInstrument.premises}</Typography>
                </div>
                <div className={classes.formHeaderItem}>
                  <Typography color="textSecondary" variant="caption">
                    RI Type
                  </Typography>
                  <Typography>{regulatoryInstrument.riType}</Typography>
                </div>
                <div className={classes.formHeaderItem}>
                  <Typography color="textSecondary" variant="caption">
                    RI Number
                  </Typography>
                  <Typography>{regulatoryInstrument.riNumber}</Typography>
                </div>
              </div>

              {regulatoryInstrument.cdImportExport && (
                <div className={classes.gridWrapper}>
                  <div className={classes.grid}>
                    <div>
                      <div className={classes.gridItem}>Product</div>
                      <div className={classes.gridItem}>Quantity Licensed</div>
                      <div className={classes.gridItem}>
                        Quantity Imported / Exported
                      </div>
                    </div>

                    <Row
                      name="quantityLicenseeDeclared"
                      quantityLicenced={
                        regulatoryInstrument.cdImportExport.quantityLicenced
                      }
                      productName={
                        regulatoryInstrument.cdImportExport.productName
                      }
                    />

                    <Row
                      name="quantityLicensee2Declared"
                      quantityLicenced={
                        regulatoryInstrument.cdImportExport.quantity2Licenced
                      }
                      productName={
                        regulatoryInstrument.cdImportExport.product2Name
                      }
                    />

                    <Row
                      name="quantityLicensee3Declared"
                      quantityLicenced={
                        regulatoryInstrument.cdImportExport.quantity3Licenced
                      }
                      productName={
                        regulatoryInstrument.cdImportExport.product3Name
                      }
                    />

                    <Row
                      name="quantityLicensee4Declared"
                      quantityLicenced={
                        regulatoryInstrument.cdImportExport.quantity4Licenced
                      }
                      productName={
                        regulatoryInstrument.cdImportExport.product4Name
                      }
                    />
                  </div>
                </div>
              )}

              <DatePickerField
                required
                name="actualImportExportDate"
                className={classes.select}
                format="dd/MM/yyyy"
                minDate={new Date("2019-01-01")}
                label="Date Imported / Exported"
              />

              <div className={classes.buttonWrapper}>
                <SubmitButton label="Submit" />
                <ResetButton label="Reset" />
                <Button
                  onClick={() => {
                    history.push(`${routes.HOME.url}`);
                  }}
                  id="close"
                >
                  Close
                </Button>
              </div>
            </RxForm>
          ) : (
            <Typography align="center" color="primary">
              Enter your regulatory instrument number to get started
            </Typography>
          )}
        </Card>
      </div>
    </PageRoot>
  );
};

const mapDispatchToProps = {
  getInstrument: regulatoryInstrumentActions.getRegulatoryInstrument,
  submitReport: regulatoryInstrumentActions.submitImportExportReport
};

const mapStateToProps = ({ regulatoryInstrument }: any, ownProps: Props) => {
  const {
    params: { id }
  }: any = ownProps.match;
  const instrument = regulatoryInstrument.instruments.find(
    (instrument: RegulatoryInstrument) => instrument.cmAcmCaseIdentifier === id
  );

  return {
    regulatoryInstrument: instrument,
    error: regulatoryInstrument.loading,
    loading: regulatoryInstrument.loading
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ActiveRegulatoryInstrumentList);
